// ─────────────────────────────────────────────────────────────────────────────
// P18-ter — botón "Descargar reporte global" de Reportería → Históricos. A diferencia de
// `CampaignRowActions` (una fila, una campaña), este exporta TODAS las campañas del alcance en un
// solo Excel, calcado de `03 - Historicos.xlsx` (hojas "Resumen Ejecutivo" + "Definiciones" +
// "Campañas Históricas").
//
// El backend (`GET campaigns-export-global`) ya resuelve el agregado deduplicado (Audiencia/
// Participantes/Staff/Frontline como UNIÓN de campañas, no suma ingenua — ver doc-comment de
// `CampaignsGlobalSummaryDto`) y una fila por campaña con el MISMO cálculo que `campaign-summary`/
// `campaign-export`. Este componente solo prepara los datos: zipea esas filas contra el catálogo de
// campañas que `HistoryTab` ya tiene cargado (Nombre/Descripción/Tipo/Estado/Alcance — NO viajan en
// el DTO del backend, mismo criterio que `CampaignRowActions`) y los traduce al idioma activo.
//
// La forma visual de las tres hojas (paleta, banner, anchos, formatos numéricos y bordes, todos
// medidos de la plantilla del cliente) vive en `historicosExcel.ts`.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast, useToast } from '@/components/common';
import { recognitionReportsApi, getCampaignName, getRecognitionErrorMessage } from '@/api/recognition';
import type { CampaignDto } from '@/api/recognition';
import { exportMultiSheetExcel, type ExcelCellValue } from '@/utils/exportExcelMultiSheet';
import { useOuNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import { buildHistoricosExcelSheets, type FilaCampanaHistorico } from './historicosExcel';
import { estadoVisible } from './HistoryTab';

const SIN_DATO = '—';
const NOMBRE_ARCHIVO = 'reporteria-campanas-global';

/** `canExportData` default `true`: ver nota en `DailyTab.tsx`/`CampaignRowActions.tsx`. */
export function CampaignsGlobalExportButton(
  { campaigns, canExportData = true }: Readonly<{ campaigns: CampaignDto[]; canExportData?: boolean }>,
) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];
  const { toast, showError } = useToast();
  const { resolveOuName } = useOuNameResolver();
  const [isDownloading, setIsDownloading] = useState(false);

  /**
   * "Duración de la campaña" en meses redondos — mismo dato que la hoja de referencia del cliente
   * ("3 meses", "4 meses"…), resuelto con la forma plural del idioma activo.
   */
  function duracionTexto(inicio: string | null, fin: string | null): string {
    if (!inicio || !fin) return SIN_DATO;
    const desde = new Date(inicio);
    const hasta = new Date(fin);
    let meses = (hasta.getFullYear() - desde.getFullYear()) * 12
      + (hasta.getMonth() - desde.getMonth());
    if (hasta.getDate() < desde.getDate()) meses -= 1;
    return t('reports.history.excel.durationMonths', { count: Math.max(meses, 0) });
  }

  function formatFecha(fecha: string | null): string {
    return fecha ? new Date(fecha).toLocaleDateString(lang) : SIN_DATO;
  }

  async function handleDownload() {
    setIsDownloading(true);
    try {
      const dto = await recognitionReportsApi.getCampaignsGlobalExport();
      const filaPorCampana = new Map(dto.campanas.map((fila) => [fila.campaignId, fila]));

      // El guion de «sin dato» sustituye al 0 cuando el agregado está suprimido por anonimato: un
      // cero se leería como "nadie participó" en vez de "no se puede mostrar".
      const cifraGlobal = (valor: number): ExcelCellValue => (dto.suprimido ? SIN_DATO : valor);

      const campanas: FilaCampanaHistorico[] = campaigns.map((campaign) => {
        const fila = filaPorCampana.get(campaign.id);
        const cifra = (valor: number | undefined): ExcelCellValue =>
          (fila?.suprimido ? SIN_DATO : (valor ?? SIN_DATO));

        return {
          nombre: getCampaignName(campaign, lang),
          tipo: t(`reports.campaigns.type.${campaign.tipo}`),
          audienciaOu: campaign.alcance.length === 0
            ? t('reports.common.scopeAll')
            : campaign.alcance.map(resolveOuName).join(', '),
          audiencia: fila?.audiencia ?? SIN_DATO,
          participantes: cifra(fila?.participantes),
          participantesStaff: cifra(fila?.participantesStaff),
          participantesFrontline: cifra(fila?.participantesFrontline),
          duracion: duracionTexto(campaign.fechaInicio, campaign.fechaFin),
          reconocimientos: cifra(fila?.reconocimientos),
          expiracion: formatFecha(campaign.fechaFin),
          estadoKey: estadoVisible(campaign.estado),
          estado: t(`reports.history.status.${estadoVisible(campaign.estado)}`),
          descripcion: campaign.descripcion?.[lang] ?? campaign.descripcion?.es ?? SIN_DATO,
        };
      });

      const sheets = buildHistoricosExcelSheets({
        traducir: (clave: string, opciones?: Record<string, unknown>) =>
          t(`reports.history.${clave}`, opciones ?? {}),
        kpis: {
          totalCampanas: dto.totalCampanas,
          totalReconocimientos: cifraGlobal(dto.totalReconocimientos),
          // La Audiencia NO se suprime: es el denominador configurado de la campaña, no un conteo
          // de personas identificables (mismo criterio que `CampaignSummaryDto.audiencia`).
          audienciaTotal: dto.audienciaTotal,
          participantes: cifraGlobal(dto.participantes),
          participantesStaff: cifraGlobal(dto.participantesStaff),
          participantesFrontline: cifraGlobal(dto.participantesFrontline),
          porcentajeParticipacion: cifraGlobal(dto.porcentajeParticipacion),
          promedioPorParticipante: cifraGlobal(dto.promedioPorParticipante),
        },
        campanas,
        filtro: t('reports.history.excel.filterAllCampaigns'),
        generado: new Date().toLocaleString(lang),
      });

      await exportMultiSheetExcel({ fileName: NOMBRE_ARCHIVO, sheets });
    } catch (error: unknown) {
      showError(getRecognitionErrorMessage(error, t('reports.history.downloadGlobalError')));
      if (import.meta.env.DEV) console.error('No se pudo descargar el reporte global de campañas:', error);
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <>
      {canExportData && (
        <button type="button" className="rr-btn" onClick={handleDownload} disabled={isDownloading}>
          <i
            className={isDownloading ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'}
            aria-hidden="true"
          />{' '}
          {isDownloading ? t('reports.shine.exporting') : t('reports.history.downloadGlobalExcel')}
        </button>
      )}
      <Toast toast={toast} />
    </>
  );
}
